import React, { useState } from 'react';
import { useApp } from '../App';
import TabBar from '../components/ui/TabBar';
import { F, FArea, FToggle, SectionHead, SaveBtn } from './fields';
import { AccordionSection } from '../components/ui/Accordion';

export default function HouseInfo() {
  const { familyData, setFamilyData } = useApp() || {};
  const familyId = familyData?.id;
  const [tab, setTab] = useState('location');
  const [saving, setSaving] = useState(false);
  const [saved,  setSaved]  = useState(null);

  // ── Location ──────────────────────────────────────────────────────────────
  const [loc, setLoc] = useState(familyData?.houseData?.location || {});

  // ── Safety info ───────────────────────────────────────────────────────────
  // Editor state merges the shared safetyInfo doc with the owner-only
  // private/security doc (alarm/gate codes); save() splits them back apart.
  const [safety, setSafety] = useState({
    ...(familyData?.houseData?.safetyInfo || {}),
    ...(familyData?.houseData?.security || {}),
  });

  // ── Emergency services ────────────────────────────────────────────────────
  const [services, setServices] = useState(familyData?.houseData?.emergencyServices || {});

  // ── Contacts ──────────────────────────────────────────────────────────────
  const [contacts, setContacts]       = useState(familyData?.houseData?.contacts || []);
  const [editingContact, setEditingContact] = useState(null); // null | '__new__' | contact_id

  // ── Pets ──────────────────────────────────────────────────────────────────
  const [pets, setPets]       = useState(familyData?.houseData?.pets || []);
  const [editingPet, setEditingPet] = useState(null);

  // ── Norms & routines (shared with caregivers) ─────────────────────────────
  const [norms, setNorms] = useState(familyData?.houseData?.norms || {});
  const routines = norms.routines || {};
  const setRoutine = (k, v) => setNorms(n => ({ ...n, routines: { ...(n.routines || {}), [k]: v } }));

  // Alarm/gate codes are owner-only — they live in private/security, not the
  // shared safetyInfo doc that token links can read (wifi stays shared).
  const SECURITY_KEYS = ['alarm_code', 'alarm_company_name', 'alarm_company_phone', 'garage_gate_code'];

  async function save(section, data) {
    if (!familyId) return;
    setSaving(true);
    try {
      const { saveLocation, saveSafetyInfo, saveEmergencyServices, saveSecurity, saveNorms } = await import('../lib/houseData');
      if (section === 'location')  await saveLocation(familyId, data);
      if (section === 'norms') {
        await saveNorms(familyId, data);
        setFamilyData(prev => ({ ...prev, houseData: { ...(prev.houseData || {}), norms: data } }));
        setSaved(section);
        setTimeout(() => setSaved(null), 2500);
        setSaving(false);
        return;
      }
      if (section === 'safety') {
        const shared = { ...data };
        const secret = {};
        for (const k of SECURITY_KEYS) {
          secret[k] = shared[k] || '';
          shared[k] = ''; // blank out in the shared doc (fsSet merges, so old values must be overwritten)
        }
        try {
          await saveSafetyInfo(familyId, shared);
        } catch (err) {
          alert(`Save failed (house basics): ${err.code || 'unknown'} — ${err.message}`);
          setSaving(false);
          return;
        }
        try {
          await saveSecurity(familyId, secret);
        } catch (err) {
          alert(`Save failed (security codes): ${err.code || 'unknown'} — ${err.message}`);
          setSaving(false);
          return;
        }
        setFamilyData(prev => ({
          ...prev,
          houseData: { ...(prev.houseData || {}), safetyInfo: shared, security: secret },
        }));
        setSaved(section);
        setTimeout(() => setSaved(null), 2500);
        setSaving(false);
        return;
      }
      if (section === 'services')  await saveEmergencyServices(familyId, data);
      setFamilyData(prev => ({
        ...prev,
        houseData: { ...(prev.houseData || {}), [section === 'location' ? 'location' : 'emergencyServices']: data }
      }));
      setSaved(section);
      setTimeout(() => setSaved(null), 2500);
    } catch (err) {
      alert(`Save failed (${section}): ${err.code || 'unknown'} — ${err.message}`);
    }
    setSaving(false);
  }

  async function saveContactFn(contact) {
    if (!familyId) return;
    setSaving(true);
    try {
      const { saveContact, sortContacts } = await import('../lib/houseData');
      await saveContact(familyId, contact);
      const updated = contacts.find(c => c.contact_id === contact.contact_id)
        ? contacts.map(c => c.contact_id === contact.contact_id ? contact : c)
        : [...contacts, contact];
      const sorted = sortContacts(updated);
      setContacts(sorted);
      setFamilyData(prev => ({ ...prev, houseData: { ...(prev.houseData || {}), contacts: sorted } }));
      setEditingContact(null);
    } catch (err) {
      alert(`Save failed (contact): ${err.code || 'unknown'} — ${err.message}`);
    }
    setSaving(false);
  }

  async function deleteContactFn(contactId) {
    if (!familyId) return;
    try {
      const { deleteContact } = await import('../lib/houseData');
      await deleteContact(familyId, contactId);
      const updated = contacts.filter(c => c.contact_id !== contactId);
      setContacts(updated);
      setFamilyData(prev => ({ ...prev, houseData: { ...(prev.houseData || {}), contacts: updated } }));
    } catch (err) {
      alert(`Delete failed (contact): ${err.code || 'unknown'} — ${err.message}`);
    }
  }

  async function savePetFn(pet) {
    if (!familyId) return;
    setSaving(true);
    try {
      const { savePet } = await import('../lib/houseData');
      await savePet(familyId, pet);
      const updated = pets.find(p => p.pet_id === pet.pet_id)
        ? pets.map(p => p.pet_id === pet.pet_id ? pet : p)
        : [...pets, pet];
      setPets(updated);
      setFamilyData(prev => ({ ...prev, houseData: { ...(prev.houseData || {}), pets: updated } }));
      setEditingPet(null);
    } catch (err) {
      alert(`Save failed (pet): ${err.code || 'unknown'} — ${err.message}`);
    }
    setSaving(false);
  }

  async function deletePetFn(petId) {
    if (!familyId) return;
    try {
      const { deletePet } = await import('../lib/houseData');
      await deletePet(familyId, petId);
      const updated = pets.filter(p => p.pet_id !== petId);
      setPets(updated);
      setFamilyData(prev => ({ ...prev, houseData: { ...(prev.houseData || {}), pets: updated } }));
    } catch (err) {
      alert(`Delete failed (pet): ${err.code || 'unknown'} — ${err.message}`);
    }
  }

  const TABS = [
    { id: 'location',  label: '🪵 Location' },
    { id: 'contacts',  label: '📞 Contacts' },
    { id: 'services',  label: '🔥 Emergency' },
    { id: 'safety',    label: '🌾 Safety' },
    { id: 'norms',     label: '🌱 Norms' },
    { id: 'pets',      label: '🐾 Pets' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <p style={{ fontSize: 13, color: 'var(--gray)', marginBottom: 12 }}>
        Shown to caregivers and guests in their view. Keep current.
      </p>

      {/* Tab bar */}
      <TabBar tabs={TABS} value={tab} onChange={setTab} style={{ marginBottom: 16 }} />

      {/* ── LOCATION ─────────────────────────────────────────────────────── */}
      {tab === 'location' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <F label="Street address" value={loc.street_line_1 || ''} onChange={v => setLoc(s => ({...s, street_line_1: v}))} placeholder="69 Knollwood Dr" />
          <F label="Apt / Unit" value={loc.street_line_2 || ''} onChange={v => setLoc(s => ({...s, street_line_2: v}))} />
          <div className="form-row">
            <F label="City" value={loc.city || ''} onChange={v => setLoc(s => ({...s, city: v}))} placeholder="Cherry Hill" />
            <F label="State" value={loc.state || ''} onChange={v => setLoc(s => ({...s, state: v}))} placeholder="NJ" />
            <F label="ZIP" value={loc.zip_code || ''} onChange={v => setLoc(s => ({...s, zip_code: v}))} placeholder="08002" />
          </div>
          <F label="Cross street / landmark" value={loc.cross_street || ''} onChange={v => setLoc(s => ({...s, cross_street: v}))} placeholder="Near Knollwood Park entrance" />
          <F label="Fire evacuation meeting point" value={loc.fire_meeting_point || ''} onChange={v => setLoc(s => ({...s, fire_meeting_point: v}))} placeholder="End of driveway" />
          <SaveBtn onSave={() => save('location', loc)} saving={saving} saved={saved === 'location'} />
        </div>
      )}

      {/* ── CONTACTS ─────────────────────────────────────────────────────── */}
      {tab === 'contacts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {editingContact !== null ? (
            <ContactForm
              contact={editingContact === '__new__' ? {} : contacts.find(c => c.contact_id === editingContact)}
              onSave={saveContactFn}
              onCancel={() => setEditingContact(null)}
            />
          ) : (
            <>
              {contacts.length === 0 && <p style={{ fontSize: 13, color: 'var(--sage)' }}>No contacts yet</p>}
              {contacts.map(c => (
                <div key={c.contact_id} className="edit-list-row">
                  <div className="edit-list-row__info">
                    <div className="edit-list-row__name">{c.full_name}
                      <span className="badge badge--gray" style={{ marginLeft: 6, fontSize: 10 }}>{c.contact_role}</span>
                    </div>
                    <div className="edit-list-row__sub">{c.mobile_phone}{c.relationship_to_child ? ` · ${c.relationship_to_child}` : ''}</div>
                  </div>
                  <div className="cp-row-actions">
                    <button className="cp-action-btn" onClick={() => setEditingContact(c.contact_id)}>✏️</button>
                    <button className="cp-action-btn cp-action-btn--danger" onClick={() => deleteContactFn(c.contact_id)}>✕</button>
                  </div>
                </div>
              ))}
              <button className="btn btn--secondary btn--small" onClick={() => setEditingContact('__new__')}>+ Add Contact</button>
            </>
          )}
        </div>
      )}

      {/* ── EMERGENCY SERVICES ───────────────────────────────────────────── */}
      {tab === 'services' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <p style={{ fontSize: 12, color: 'var(--sage)' }}>Enter your local emergency service numbers. These appear prominently in caregiver/guest views.</p>
          {['police','fire','ambulance','hospital','poison_control','vet_emergency'].map(type => {
            const cfg = { police: {icon:'🚔',label:'Police (non-emergency)'}, fire: {icon:'🔥',label:'Fire Department'}, ambulance: {icon:'🚑',label:'Ambulance / EMS'}, hospital: {icon:'🏥',label:'Nearest Hospital'}, poison_control: {icon:'☠️',label:'Poison Control'}, vet_emergency: {icon:'🐾',label:'Emergency Vet (24/7)'} }[type] || {icon:'📞',label:type};
            const svc = services[type] || {};
            return (
              <AccordionSection key={type} id={type} icon={cfg.icon} title={cfg.label}>
                <div className="form-row">
                  <F label="Name" value={svc.service_name || ''} onChange={v => setServices(s => ({...s, [type]: {...(s[type]||{}), service_type: type, service_name: v}}))} placeholder={cfg.label} />
                  <F label="Phone" value={svc.primary_phone || ''} onChange={v => setServices(s => ({...s, [type]: {...(s[type]||{}), primary_phone: v}}))} placeholder="(856) 555-0100" />
                </div>
                <F label="Address (for navigation)" value={svc.address || ''} onChange={v => setServices(s => ({...s, [type]: {...(s[type]||{}), address: v}}))} />
              </AccordionSection>
            );
          })}
          <F label="WiFi network name" value={safety.wifi_ssid || ''} onChange={v => setSafety(s => ({...s, wifi_ssid: v}))} />
          <F label="WiFi password" value={safety.wifi_password || ''} onChange={v => setSafety(s => ({...s, wifi_password: v}))} />
          <SaveBtn onSave={async () => { await save('services', services); await save('safety', safety); }} saving={saving} saved={saved === 'services' || saved === 'safety'} />
        </div>
      )}

      {/* ── SAFETY INFO ──────────────────────────────────────────────────── */}
      {tab === 'safety' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <AccordionSection id="access" icon="🔐" title="Access & Codes">
            <F label="Alarm code (entry/disarm)" value={safety.alarm_code || ''} onChange={v => setSafety(s => ({...s, alarm_code: v}))} />
            <F label="Alarm company" value={safety.alarm_company_name || ''} onChange={v => setSafety(s => ({...s, alarm_company_name: v}))} />
            <F label="Alarm company phone" value={safety.alarm_company_phone || ''} onChange={v => setSafety(s => ({...s, alarm_company_phone: v}))} />
            <F label="Garage / gate code" value={safety.garage_gate_code || ''} onChange={v => setSafety(s => ({...s, garage_gate_code: v}))} />
            <F label="Spare key location" value={safety.spare_key_location || ''} onChange={v => setSafety(s => ({...s, spare_key_location: v}))} placeholder="Under front porch planter" />
          </AccordionSection>

          <AccordionSection id="equipment" icon="🧰" title="Safety Equipment">
            <F label="First aid kit location" value={safety.first_aid_kit_location || ''} onChange={v => setSafety(s => ({...s, first_aid_kit_location: v}))} />
            <F label="Fire extinguisher location" value={safety.fire_extinguisher_location || ''} onChange={v => setSafety(s => ({...s, fire_extinguisher_location: v}))} />
            <F label="Circuit breaker location" value={safety.circuit_breaker_location || ''} onChange={v => setSafety(s => ({...s, circuit_breaker_location: v}))} />
            <F label="Gas shutoff location" value={safety.gas_shutoff_location || ''} onChange={v => setSafety(s => ({...s, gas_shutoff_location: v}))} />
            <F label="AED location (if applicable)" value={safety.aed_location || ''} onChange={v => setSafety(s => ({...s, aed_location: v}))} />
          </AccordionSection>

          <AccordionSection id="rules" icon="📋" title="House Rules">
            <F label="Off-limits areas" value={(safety.off_limits_areas || []).join(', ')} onChange={v => setSafety(s => ({...s, off_limits_areas: v.split(',').map(x => x.trim()).filter(Boolean)}))} placeholder="e.g. Master bedroom, basement" />
            <FToggle label="No visitors policy" value={!!safety.no_visitor_policy} onChange={v => setSafety(s => ({...s, no_visitor_policy: v}))} />
            {safety.no_visitor_policy && (
              <FArea label="Visitor policy notes" value={safety.visitor_policy_notes || ''} onChange={v => setSafety(s => ({...s, visitor_policy_notes: v}))} />
            )}
            <FArea label="General notes for caregivers" value={safety.notes_for_sitter || ''} onChange={v => setSafety(s => ({...s, notes_for_sitter: v}))} placeholder="Routines, where things are, what to do if…" />
          </AccordionSection>
          <SaveBtn onSave={() => save('safety', safety)} saving={saving} saved={saved === 'safety'} />
        </div>
      )}

      {/* ── NORMS & ROUTINES ─────────────────────────────────────────────── */}
      {tab === 'norms' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          <p style={{ fontSize: 12, color: 'var(--sage)' }}>
            How the house runs — caregivers see this in their House tab.
          </p>
          <SectionHead>🕰 Daily routines</SectionHead>
          <FArea label="Morning" value={routines.morning || ''} onChange={v => setRoutine('morning', v)} placeholder="Wake 6:45, breakfast by 7:15, bus at 8:05" />
          <FArea label="After school" value={routines.after_school || ''} onChange={v => setRoutine('after_school', v)} placeholder="Snack, homework before screens, outside play" />
          <FArea label="Evening" value={routines.evening || ''} onChange={v => setRoutine('evening', v)} placeholder="Dinner 6, baths, reading, lights out by bedtime per kid" />
          <SectionHead>📱 Defaults</SectionHead>
          <F label="Screen time defaults" value={norms.screen_time_defaults || ''} onChange={v => setNorms(n => ({ ...n, screen_time_defaults: v }))} placeholder="1h on school days, 2h weekends" />
          <F label="Visitor norms" value={norms.visitor_norms || ''} onChange={v => setNorms(n => ({ ...n, visitor_norms: v }))} placeholder="Friends OK until 5pm, parents must confirm" />
          <SectionHead>📋 House rules</SectionHead>
          <FArea label="House rules (one per line)" value={(norms.house_rules || []).join('\n')}
            onChange={v => setNorms(n => ({ ...n, house_rules: v.split('\n').map(x => x.trim()).filter(Boolean) }))}
            placeholder={'Shoes off at the door\nNo food upstairs'} />
          <SaveBtn onSave={() => save('norms', norms)} saving={saving} saved={saved === 'norms'} />
        </div>
      )}

      {/* ── PETS ─────────────────────────────────────────────────────────── */}
      {tab === 'pets' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {editingPet !== null ? (
            <PetForm
              pet={editingPet === '__new__' ? {} : pets.find(p => p.pet_id === editingPet)}
              onSave={savePetFn}
              onCancel={() => setEditingPet(null)}
            />
          ) : (
            <>
              {pets.length === 0 && <p style={{ fontSize: 13, color: 'var(--sage)' }}>No pets added yet</p>}
              {pets.map(p => (
                <div key={p.pet_id} className="edit-list-row">
                  <div className="edit-list-row__info">
                    <div className="edit-list-row__name">{p.name}
                      <span className="badge badge--gray" style={{ marginLeft: 6, fontSize: 10 }}>{p.species}</span>
                    </div>
                    <div className="edit-list-row__sub">{p.breed || ''}{p.primary_vet_name ? ` · Vet: ${p.primary_vet_name}` : ''}</div>
                  </div>
                  <div className="cp-row-actions">
                    <button className="cp-action-btn" onClick={() => setEditingPet(p.pet_id)}>✏️</button>
                    <button className="cp-action-btn cp-action-btn--danger" onClick={() => deletePetFn(p.pet_id)}>✕</button>
                  </div>
                </div>
              ))}
              <button className="btn btn--secondary btn--small" onClick={() => setEditingPet('__new__')}>+ Add Pet</button>
            </>
          )}
        </div>
      )}
    </div>
  );
}


// ── CONTACT FORM ──────────────────────────────────────────────────────────────
function ContactForm({ contact = {}, onSave, onCancel }) {
  const { familyData } = useApp() || {};
  const caregivers = (familyData?.sitters || []).filter(x => x.active !== false);
  const [form, setForm] = useState({
    contact_id: contact.contact_id || `contact_${Date.now()}`,
    contact_role: contact.contact_role || 'parent',
    full_name: contact.full_name || '',
    mobile_phone: contact.mobile_phone || '',
    secondary_phone: contact.secondary_phone || '',
    relationship_to_child: contact.relationship_to_child || '',
    destination_name: contact.destination_name || '',
    destination_address: contact.destination_address || '',
    expected_return_time: contact.expected_return_time || '',
    home_address: contact.home_address || '',
    notes: contact.notes || '',
    caregiverId: contact.caregiverId || '',
  });
  const R = v => setForm(f => ({...f, ...v}));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--cream-light)', borderRadius: 10, padding: 14 }}>
      <div className="form-row">
        <div>
          <label className="form-label" style={{ marginTop: 0 }}>Role</label>
          <select className="input" value={form.contact_role} onChange={e => R({ contact_role: e.target.value })}>
            {['parent','guardian','backup','neighbor','relative'].map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <F label="Full name" value={form.full_name} onChange={v => R({ full_name: v })} />
      </div>
      <div className="form-row">
        <F label="Mobile phone" value={form.mobile_phone} onChange={v => R({ mobile_phone: v })} placeholder="+1 (856) 555-0100" />
        <F label="Secondary phone" value={form.secondary_phone} onChange={v => R({ secondary_phone: v })} />
      </div>
      <F label="Relationship to child" value={form.relationship_to_child} onChange={v => R({ relationship_to_child: v })} placeholder="Mother, Aunt, etc." />
      {caregivers.length > 0 && (
        <div>
          <label className="form-label">Same person as a caregiver? (avoids double entry)</label>
          <select className="input" value={form.caregiverId} onChange={e => R({ caregiverId: e.target.value })}>
            <option value="">Not a caregiver</option>
            {caregivers.map(c => <option key={c.id} value={c.id}>{c.firstName} {c.lastName || ''}</option>)}
          </select>
        </div>
      )}
      <div className="form-row">
        <F label="Where they'll be tonight" value={form.destination_name} onChange={v => R({ destination_name: v })} placeholder="Chez Henri restaurant" />
        <F label="Expected return" value={form.expected_return_time} onChange={v => R({ expected_return_time: v })} type="datetime-local" />
      </div>
      <F label="Destination address (if phone fails)" value={form.destination_address} onChange={v => R({ destination_address: v })} />
      <F label="Home address (for neighbors)" value={form.home_address} onChange={v => R({ home_address: v })} placeholder="71 Knollwood Dr" />
      <FArea label="Notes" value={form.notes} onChange={v => R({ notes: v })} placeholder="Has a spare key, speaks Spanish" />
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button className="btn btn--primary" onClick={() => onSave(form)} disabled={!form.full_name.trim()}>Save contact</button>
        <button className="btn btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── PET FORM ──────────────────────────────────────────────────────────────────
function PetForm({ pet = {}, onSave, onCancel }) {
  const [form, setForm] = useState({
    pet_id: pet.pet_id || `pet_${Date.now()}`,
    name: pet.name || '',
    species: pet.species || 'dog',
    breed: pet.breed || '',
    sex: pet.sex || 'female',
    spayed_neutered: pet.spayed_neutered ?? true,
    vaccinations_current: pet.vaccinations_current ?? true,
    microchip_id: pet.microchip_id || '',
    weight_lbs: pet.weight_lbs || '',
    known_allergies: (pet.known_allergies || []).join(', '),
    medical_conditions: (pet.medical_conditions || []).join(', '),
    current_medications: pet.current_medications || '',
    feeding_schedule: pet.feeding_schedule || '',
    food_brand_and_type: pet.food_brand_and_type || '',
    leash_crate_location: pet.leash_crate_location || '',
    outdoor_access_rules: pet.outdoor_access_rules || '',
    furniture_rules: pet.furniture_rules || '',
    bedtime_crate_routine: pet.bedtime_crate_routine || '',
    behavioral_notes: pet.behavioral_notes || '',
    primary_vet_name: pet.primary_vet_name || '',
    primary_vet_phone: pet.primary_vet_phone || '',
    primary_vet_address: pet.primary_vet_address || '',
    emergency_vet_name: pet.emergency_vet_name || '',
    emergency_vet_phone: pet.emergency_vet_phone || '',
    emergency_vet_address: pet.emergency_vet_address || '',
  });
  const R = v => setForm(f => ({...f, ...v}));
  function toArray(s) { return s.split(',').map(x => x.trim()).filter(Boolean); }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--cream-light)', borderRadius: 10, padding: 14 }}>
      <div className="form-row">
        <F label="Name" value={form.name} onChange={v => R({ name: v })} />
        <div>
          <label className="form-label" style={{ marginTop: 0 }}>Species</label>
          <select className="input" value={form.species} onChange={e => R({ species: e.target.value })}>
            {['dog','cat','bird','other'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>
      <div className="form-row">
        <F label="Breed" value={form.breed} onChange={v => R({ breed: v })} />
        <F label="Weight (lbs)" value={form.weight_lbs} onChange={v => R({ weight_lbs: v })} />
      </div>
      <SectionHead>🏥 Vet & Medical</SectionHead>
      <F label="Known allergies (comma separated, or 'None')" value={form.known_allergies} onChange={v => R({ known_allergies: v })} />
      <F label="Medical conditions" value={form.medical_conditions} onChange={v => R({ medical_conditions: v })} />
      <F label="Current medications" value={form.current_medications} onChange={v => R({ current_medications: v })} />
      <F label="Microchip ID" value={form.microchip_id} onChange={v => R({ microchip_id: v })} />
      <div className="form-row">
        <F label="Primary vet name" value={form.primary_vet_name} onChange={v => R({ primary_vet_name: v })} />
        <F label="Primary vet phone" value={form.primary_vet_phone} onChange={v => R({ primary_vet_phone: v })} />
      </div>
      <F label="Primary vet address" value={form.primary_vet_address} onChange={v => R({ primary_vet_address: v })} />
      <div className="form-row">
        <F label="Emergency vet (24/7)" value={form.emergency_vet_name} onChange={v => R({ emergency_vet_name: v })} />
        <F label="Emergency vet phone" value={form.emergency_vet_phone} onChange={v => R({ emergency_vet_phone: v })} />
      </div>
      <F label="Emergency vet address" value={form.emergency_vet_address} onChange={v => R({ emergency_vet_address: v })} />
      <SectionHead>🦮 Daily Care</SectionHead>
      <F label="Feeding schedule" value={form.feeding_schedule} onChange={v => R({ feeding_schedule: v })} placeholder="7 AM & 6 PM, 1/2 cup" />
      <F label="Food brand & location" value={form.food_brand_and_type} onChange={v => R({ food_brand_and_type: v })} />
      <F label="Leash / crate location" value={form.leash_crate_location} onChange={v => R({ leash_crate_location: v })} />
      <F label="Outdoor access rules" value={form.outdoor_access_rules} onChange={v => R({ outdoor_access_rules: v })} placeholder="Yard only off-leash" />
      <F label="Furniture rules" value={form.furniture_rules} onChange={v => R({ furniture_rules: v })} />
      <F label="Bedtime / crate routine" value={form.bedtime_crate_routine} onChange={v => R({ bedtime_crate_routine: v })} />
      <FArea label="Behavioral notes" value={form.behavioral_notes} onChange={v => R({ behavioral_notes: v })} placeholder="Anxiety triggers, stranger behavior…" />
      <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
        <button className="btn btn--primary" onClick={() => onSave({
          ...form,
          known_allergies: toArray(form.known_allergies) || ['None'],
          medical_conditions: toArray(form.medical_conditions),
        })} disabled={!form.name.trim()}>Save pet</button>
        <button className="btn btn--ghost" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}



